import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { SurveyType } from '../../../../../shared/constants';
import {
  formatSurveyAttentionRange,
  SURVEY_TYPE_DISPLAY_NAME,
} from '../../../../../shared/surveys';
import NoDataCard from '../../../../Dashboard/detailPanel/NoDataCard';
import type {
  SurveyInsightsGranularity,
  SurveyInsightsPeriod,
} from '../../../../../store/hubOwner/types';
import type { FacilitySurveyInsightsBlock } from '../../../../../store/facility/facilityTypes';
import InsightsPanel from '../../../../Patients/PatientDetails/components/InsightsTab/InsightsPanel';
import StatCardsGrid from '../../../../Patients/PatientDetails/components/InsightsTab/StatCardsGrid';
import SurveyTrendChart from '../../../../Patients/PatientDetails/components/InsightsTab/SurveyTrendChart';
import SurveyDistribution from '../../../../Patients/PatientDetails/components/InsightsTab/SurveyDistribution';
import {
  buildTrendPoints,
  formatPeriodRange,
} from '../../../../Patients/PatientDetails/components/InsightsTab/surveyInsights';
import type { StatCardsStatus } from '../../../../Patients/PatientDetails/components/InsightsTab/surveyStatCards';
import SurveyParticipation from './SurveyParticipation';
import LowestScoresTable from './LowestScoresTable';
import {
  buildFacilityStatCards,
  computeFacilitySurveyStats,
  selectLowestScoreRows,
} from './facilitySurveyInsights';

interface FacilitySurveyInsightsViewProps {
  surveyType: SurveyType;
  // Window metrics for this type from GET /facility/:id/survey_insights.
  block?: FacilitySurveyInsightsBlock | null;
  period?: SurveyInsightsPeriod | null;
  // Patients currently assigned to the location; the participation
  // denominator only for a response that predates patients_eligible.
  patientCount: number;
  windowLabel: string;
  // Bucket size requested for the window; the API's period wins when present.
  granularity: SurveyInsightsGranularity;
  // The fetch is in flight: panels show a pending state, never zeros.
  loading?: boolean;
  error?: string | null;
}

// One dashboard view for a location: stats tiles, the trend line, the score
// distribution, participation and the lowest-scoring patients for a single
// survey type over the window.
const FacilitySurveyInsightsView: React.FC<FacilitySurveyInsightsViewProps> = ({
  surveyType,
  block,
  period,
  patientCount,
  windowLabel,
  granularity,
  loading = false,
  error,
}) => {
  const stats = useMemo(
    () => computeFacilitySurveyStats(block, patientCount),
    [block, patientCount],
  );
  const points = useMemo(() => buildTrendPoints(block?.trend), [block]);
  const lowestRows = useMemo(
    () => selectLowestScoreRows(block, surveyType),
    [block, surveyType],
  );
  const name = SURVEY_TYPE_DISPLAY_NAME[surveyType];
  const question = block?.title ?? null;
  const hasWindowData = (block?.response_count ?? 0) > 0;
  const periodRange = formatPeriodRange(period);
  const windowText = periodRange
    ? `${windowLabel} (${periodRange})`
    : windowLabel;
  const status: StatCardsStatus = error
    ? 'unavailable'
    : loading
      ? 'loading'
      : 'ready';
  const cards = useMemo(
    () =>
      buildFacilityStatCards(
        stats,
        surveyType,
        windowLabel,
        period?.granularity ?? granularity,
        status,
      ),
    [stats, surveyType, windowLabel, period, granularity, status],
  );

  if (!hasWindowData && !loading && !error) {
    return <NoDataCard />;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={3}
      data-testid={`facility-survey-insights-${surveyType}`}
    >
      <StatCardsGrid cards={cards} testId="facility-survey-stats-cards" />
      <InsightsPanel
        title={`${name} score trend`}
        subtitle={question ? `${question} · ${windowText}` : windowText}
        loading={loading}
        error={error}
        testId="facility-survey-trend-panel"
      >
        {hasWindowData ? (
          <SurveyTrendChart
            points={points}
            surveyType={surveyType}
            period={period}
            average={stats.average}
          />
        ) : (
          <NoDataCard />
        )}
      </InsightsPanel>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(0, 1fr) minmax(0, 1fr)',
          },
          gap: 3,
        }}
      >
        <InsightsPanel
          title="Score distribution"
          subtitle={windowText}
          loading={loading}
          error={error}
          testId="facility-survey-distribution-panel"
        >
          <SurveyDistribution
            distribution={block?.distribution ?? []}
            surveyType={surveyType}
          />
        </InsightsPanel>
        <InsightsPanel
          title="Participation"
          subtitle={`Patients who answered the ${name} survey · ${windowText}`}
          loading={loading}
          error={error}
          testId="facility-survey-participation-panel"
        >
          <SurveyParticipation participation={stats.participation} />
        </InsightsPanel>
      </Box>
      <InsightsPanel
        title="Lowest scores"
        subtitle={`Patients whose lowest ${name} answer was ${formatSurveyAttentionRange(
          surveyType,
        )} · ${windowText} · select a patient to open their insights`}
        loading={loading}
        error={error}
        testId="facility-survey-lowest-panel"
      >
        <LowestScoresTable rows={lowestRows} surveyType={surveyType} />
      </InsightsPanel>
    </Box>
  );
};

export default FacilitySurveyInsightsView;
